import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, Clock, Layers, PlayCircle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };

export default function CourseCard({ course, isEnrolled }) {
  const [instructor, setInstructor] = useState(course.instructor || null);

  useEffect(() => {
    if (course.instructor || !course.instructorId) return;
    const fetchInstructor = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', course.instructorId));
        if (snap.exists()) {
          setInstructor(snap.data());
        }
      } catch (error) {
        console.error("Error fetching instructor for CourseCard:", error);
      }
    };
    fetchInstructor();
  }, [course]);

  // Calculate total duration from curriculum lessons dynamically
  let durationText = course.duration || course.durationLabel || 'غير محدد';
  if (course.curriculum && Array.isArray(course.curriculum)) {
    let totalSeconds = 0;
    course.curriculum.forEach(unit => {
      if (unit.lessons && Array.isArray(unit.lessons)) {
        unit.lessons.forEach(lesson => {
          totalSeconds += (parseInt(lesson.durationMin) || 0) * 60 + (parseInt(lesson.durationSec) || 0);
        });
      }
    });

    if (totalSeconds > 0) {
      const hrs = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      if (hrs > 0 && mins > 0) durationText = `${hrs} ساعة و ${mins} دقيقة`;
      else if (hrs > 0) durationText = `${hrs} ساعة`;
      else durationText = `${mins} دقيقة`;
    }
  }

  return (
    <Link
      to={`/courses/${course.slug || course.id}`}
      className="group block relative w-full aspect-[3/4] min-h-[400px] rounded-3xl overflow-hidden border border-slate-200 dark:border-zinc-800 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary-500/20 transition-all duration-500 font-sans"
    >
      {/* Background Image Area (The Hero) */}
      {course.thumbnail ? (
        <img
          src={course.thumbnail}
          alt={course.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 w-full h-full bg-zinc-900 flex items-center justify-center transition-transform duration-700 ease-out group-hover:scale-105">
          <BookOpen className="w-20 h-20 text-white/10" />
        </div>
      )}

      {/* Gradient Overlay for Text Readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary-600/90 via-primary-600/20 to-transparent pointer-events-none"></div>

      {/* Top Badges (Status/Level/Price) */}
      <div className="absolute top-5 left-5 right-5 flex justify-between items-start z-10 pointer-events-none">

        {/* Badges on the right visually since it's RTL context usually, so this will flex depending on dir */}
        <div className="flex flex-col gap-2 items-start">
          {isEnrolled && (
            <span className="backdrop-blur-md bg-white/10 border border-white/20 text-white text-md px-3 py-1 rounded-full font-medium">
              ✓ تم التسجيل
            </span>
          )}
          <span className="backdrop-blur-lg bg-primary-500/20 border border-white/20 text-white !text-lg px-3 py-1 rounded-lg font-medium">
            {LEVELS[course.level] || course.level || 'مبتدئ'}
          </span>
        </div>

        {/* Price Badge */}
        <span className="backdrop-blur-lg bg-primary-500/20 border border-primary-500/50 text-white !text-lg sm:text-sm px-3 py-1 rounded-lg font-bold shadow-lg">
          {course.price > 0 ? `${course.price}$` : 'مجانًا'}
        </span>
      </div>

      {/* Bottom Content Area (Glassmorphic Footer) */}
      <div className="absolute bottom-0 w-full p-5 z-10 flex flex-col justify-end">

        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 line-clamp-2 leading-snug drop-shadow-md">
          {course.title}
        </h3>

        <div className="flex items-center gap-4 mb-2 flex-wrap">
          <div className="flex items-center gap-1.5" title="مدة الدورة">
            <Clock className="w-4 h-4 text-white/80" />
            <span className="text-white/90 text-sm font-bold">{durationText}</span>
          </div>
          <div className="flex items-center gap-1.5" title="عدد الوحدات">
            <Layers className="w-4 h-4 text-white/80" />
            <span className="text-white/90 text-sm font-bold">{course.curriculum?.length || 0} وحدات</span>
          </div>
          <div className="flex items-center gap-1.5" title="عدد الدروس">
            <PlayCircle className="w-4 h-4 text-white/80" />
            <span className="text-white/90 text-sm font-bold">
              {course.curriculum?.reduce((sum, u) => sum + (u.lessons?.length || 0), 0) || 0} درس
            </span>
          </div>
        </div>

        <div className="border-t border-white/10 my-3"></div>

        {/* Meta Row */}
        <div className="flex items-center justify-between">

          {/* Right Side Info (Since RTL context puts Instructor on right visually, we align with the prompt specs. Prompt says: Right Side = Duration, Left = Instructor) */}
          {/* We'll use flex-row-reverse if we want to force right-left, but standard in RTL means flex items-center justify-between keeps them distributed. 
              To match exact prompt description (Left Avatar, Right Duration) visually in LTR or RTL: */}
          <div className="flex items-center gap-2">
            {instructor?.photoURL ? (
              <img
                src={instructor.photoURL}
                alt={instructor?.name || 'Instructor'}
                className="w-8 h-8 rounded-full border-2 border-white object-cover bg-zinc-800"
              />
            ) : (
              <div className="w-8 h-8 rounded-full border-2 border-white bg-zinc-800 flex items-center justify-center">
                <Users className="w-4 h-4 text-zinc-400" />
              </div>
            )}
            <span className="text-white text-md font-medium line-clamp-1">
              {instructor?.name || 'مدرب لباب'}
            </span>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Users className="w-4 h-4 text-white" />
            <span className="text-white text-lg font-medium">
              {course.studentsCount || 0} طالب
            </span>
          </div>

        </div>
      </div>
    </Link>
  );
}
